import {
    AbstractComponentView,
    ExternalEventListenerController,
    html
} from "../../../admin/script/views/abstract-component-view.js";
import {SettingsStorage} from "../../util/browser/settings-storage.js";
import './contact-controller.scss';

const STATE_NAME = 'pk_contact_controller_state';
const MINIMIZED_TIME = 86400000; // 24h
const SHOW_MOBILE_TIME = 5000;
const AUTO_MAXIMIZE = false;

const isMobile = typeof window.matchMedia === 'function'
    ? window.matchMedia("(max-width: 768px)").matches
    : false;

export class ContactController extends AbstractComponentView {

    maximizeTimeout;

    static properties = {
        chats: {type: Array, state: true},
        isMinimized: {type: Boolean, state: true}
    };

    constructor() {
        super();
        this.chats = [];
        this.isMinimized = isMobile;
        this.forceMaximize = isMobile;

        this.externalEventListenerController = new ExternalEventListenerController(this);
        this.externalEventListenerController.addEventListener(window, 'CookiebotOnDialogDisplay', () => {
            clearTimeout(this.maximizeTimeout);
        })
        this.externalEventListenerController.addEventListener(window, 'CookiebotOnAccept', () => {
            this.scheduleMaximize();
        });
        this.externalEventListenerController.addEventListener(window, 'CookiebotOnDecline', () => {
            this.scheduleMaximize();
        });

        this.config = new SettingsStorage(STATE_NAME);
        if (this.config.getBoolean('isMinimized') && this.config.getNumber('isMinimizedTime') + MINIMIZED_TIME > Date.now()) {
            this.isMinimized = this.config.getBoolean('isMinimized')
            this.forceMaximize = false;
        }
    }

    get hasActiveWindow() {
        for (let chat of this.chats) {
            if (chat.isActiveWindow()) {
                return true;
            }
        }
        return false;
    }

    firstUpdated() {
        this.scheduleMaximize();
        this.dispatchViewEvent('contact_controller_initialized');
    }

    updated() {
        setTimeout(() => {
            if (this.chats.length > 1) {
                this.classList.add('active');
            }
        });
    }

    scheduleMaximize() {
        clearTimeout(this.maximizeTimeout);
        if (AUTO_MAXIMIZE) {
            this.maximizeTimeout = setTimeout(() => {
                if (this.forceMaximize) {
                    this.isMinimized = false;
                }
            }, SHOW_MOBILE_TIME);
        }
    }

    addChat(chat) {
        this.chats = [
            ...this.chats,
            chat,
        ];
        chat.store.addEventListener(this, {
            'change:state': () =>{
                this.requestUpdate();
            },
        });

        this.dispatchViewEvent('contact_controller_added_chat', chat);
    }

    showChatByNamespace(namespace) {
        for (let chat of this.chats) {
            chat.toggleChatWindow(chat.props.namespace === namespace);
        }
    }

    getChatByNamespace(namespace) {
        for (let chat of this.chats) {
            if (chat.props.namespace === namespace) {
                return chat;
            }
        }
    }

    toggleMinimized(value) {
        this.isMinimized = !!value;
        this.config.set({
            isMinimized: this.isMinimized,
            isMinimizedTime: Date.now() ,
        });
    }

    template(templateContext) {
        this.classList.toggle('has-active-window', this.hasActiveWindow);
        this.classList.toggle('minimized', this.isMinimized === true);

        for (let chat of this.chats) {
            chat.chatView.$el.toggleClass('has-contact-controller', this.chats.length > 1);
        }

        if (this.chats.length <= 1) {
            return '';
        }

        const bokChatMessagesCount = this.getChatByNamespace('DEFAULT')?.getUnreadMessagesCount() || 0;
        const salesChatMessagesCount = this.getChatByNamespace('CONTACT_REQUEST')?.getUnreadMessagesCount() || 0;
        const totalMessagesCount = bokChatMessagesCount + salesChatMessagesCount;
        return html`
                <div class="contact-controller-minimized">
                    <span class="badge text-bg-light shadow rounded-pill px-3 py-2 border border-primary cursor-pointer maximize-label" @click="${() => this.toggleMinimized(false)}">
                        Skontaktuj się z nami
                    </span>
                    <button class="btn btn-primary shadow maximize-button" title="Otwórz czat ${totalMessagesCount > 0 ? `(${totalMessagesCount} nieprzeczytanych wiadomości)` : ''}" @click="${() => this.toggleMinimized(false)}">
                        <span aria-hidden="true">
                            ${totalMessagesCount > 0 ? html`
                                <i class="bi bi-envelope"></i> ${totalMessagesCount}
                                <i class="pk-pulse-animation pk-animation-3x"></i>
                            ` : html`
                                <i class="bi bi-chat-text"></i>
                            `}
                        </span>
                    </button>
                </div>
                <div class="contact-controller-body card shadow">
                    <div class="card-body pb-2 position-relative border-bottom-0">
                        <button class="btn btn-icon p-3 position-absolute top-0 end-0" title="zamknij" @click="${() => this.toggleMinimized(true)}">
                            <i class="bi bi-x" aria-hidden="true"></i>
                        </button>
                        <strong class="fs-7 d-block">
                            Szukasz <span class="fs-6 text-primary text-nowrap">oferty dla firmy?</span>
                        </strong>
                        <small class="d-block mt-n1">Skontaktuj się z nami:</small>
                    </div>
                    <div class="list-group list-group-flush border-0">
                        <a href="${this.paths.get('contact_contact_request', {auto_chat: 'off', categoryUuid: ''})}" class="list-group-item list-group-item-action text-left ps-5 d-flex align-items-center border-0">
                            <i class="bi bi-phone fs-4 me-3" aria-hidden="true"></i>
                            Telefon
                        </a>
                        <button class="list-group-item list-group-item-action text-left ps-5 d-flex align-items-center border-0" @click="${() => this.showChatByNamespace('CONTACT_REQUEST')}">
                            <i class="bi bi-chat-left-text fs-4 me-3" aria-hidden="true"></i>
                            Chat
                            ${salesChatMessagesCount > 0 ? html`
                                <span class="badge text-bg-primary rounded-pill ms-2 position-relative">
                                    <i class="pk-pulse-animation pk-animation-3x" aria-hidden="true"></i>
                                    ${salesChatMessagesCount}
                                </span>
                            ` : null}
                        </button>
                    </div>
                    <div class="card-body pb-2 mt-3 border-top border-bottom-0">
                        <strong class="fs-7 d-block">
                            Chcesz zapytać o 
                            <span class="fs-6 text-primary text-nowrap">przesyłkę?</span>
                        </strong>
                        <small class="d-block mt-n1">Skontaktuj się z nami:</small>
                    </div>
                    <div class="list-group list-group-flush border-0 mb-3">
                        <button class="list-group-item list-group-item-action text-left ps-5 d-flex align-items-center border-0" @click="${() => this.showChatByNamespace('DEFAULT')}">
                            <i class="bi bi-chat-left-text fs-4 me-3"></i>
                            Chat z BOK
                            ${bokChatMessagesCount > 0 ? html`
                                <span class="badge text-bg-primary rounded-pill ms-2 position-relative">
                                    <i class="pk-pulse-animation pk-animation-3x"></i>
                                    ${bokChatMessagesCount}
                                    <span class="sr-only"> nieprzeczytanych wiadomości</span>
                                </span>
                            ` : null}
                        </button>
                    </div>
                </div>
        `;
    }
}

window.customElements.define('pk-contact-controller', ContactController);
